import React, { useMemo, useEffect, useState } from "react";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { createAppTheme } from "./themeConfig";
import { UI_COLORS, FALLBACK_COLORS } from "./colorConstants";
import {
  getBrowserThemePreference,
  hydrateThemeFromStorage,
} from "../state/slices/theme";

/**
 * Converts hex color to RGB values
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : UI_COLORS.primaryRgb.default;
};

/**
 * Injects CSS variables into document root for Bootstrap/SCSS compatibility
 */
const injectCSSVariables = (theme) => {
  const root = document.documentElement;
  
  // Primary colors
  root.style.setProperty("--theme-primary", theme.palette.primary.main);
  root.style.setProperty("--theme-primary-light", theme.palette.primary.light || theme.palette.primary.main);
  root.style.setProperty("--theme-primary-dark", theme.palette.primary.dark || theme.palette.primary.main);
  
  // Primary focus colors for button focus states
  const primaryRgb = hexToRgb(theme.palette.primary.main);
  root.style.setProperty("--theme-primary-rgb", primaryRgb);
  
  // Create focus shadow and background colors
  const [r, g, b] = primaryRgb.split(", ").map(Number);
  root.style.setProperty("--theme-primary-focus-shadow", `rgba(${r}, ${g}, ${b}, 0.25)`);
  root.style.setProperty("--theme-primary-focus-bg", `rgba(${r}, ${g}, ${b}, 0.08)`);
  
  // Secondary colors
  root.style.setProperty("--theme-secondary", theme.palette.secondary.main);
  root.style.setProperty("--theme-secondary-light", theme.palette.secondary.light || theme.palette.secondary.main);
  root.style.setProperty("--theme-secondary-dark", theme.palette.secondary.dark || theme.palette.secondary.main);
  
  // Background colors
  root.style.setProperty("--theme-background", theme.palette.background.default);
  // Ensure background.paper always has a value (fallback to background.default if undefined)
  const backgroundPaper = theme.palette.background.paper || theme.palette.background.default || FALLBACK_COLORS.light.backgroundPaper;
  root.style.setProperty("--theme-background-paper", backgroundPaper);
  
  // Text colors
  root.style.setProperty("--theme-text-primary", theme.palette.text.primary);
  root.style.setProperty("--theme-text-secondary", theme.palette.text.secondary);
  
  // Border colors
  const borderColor = theme.palette.mode === "dark" 
    ? FALLBACK_COLORS.dark.borderColor
    : FALLBACK_COLORS.light.borderColor;
  root.style.setProperty("--theme-border-color", borderColor);
  
  
  // Bootstrap Primary/Secondary Colors
  root.style.setProperty("--bs-primary", theme.palette.primary.main);
  root.style.setProperty("--bs-secondary", theme.palette.secondary.main);
  
  // Bootstrap Body Colors (used by most Bootstrap components)
  root.style.setProperty("--bs-body-bg", theme.palette.background.default);
  root.style.setProperty("--bs-body-color", theme.palette.text.primary);
  
  // Bootstrap Background Colors
  root.style.setProperty("--bs-secondary-bg", theme.palette.background.paper);
  root.style.setProperty("--bs-tertiary-bg", theme.palette.mode === "dark" 
    ? UI_COLORS.overlay.darkTertiary
    : UI_COLORS.overlay.lightTertiary);
  
  // Bootstrap Border Variables
  root.style.setProperty("--bs-border-color", borderColor);
  root.style.setProperty("--bs-border-color-translucent", borderColor);
  
  // Bootstrap RGB values for opacity calculations
  if (theme.palette.mode === "dark") {
    root.style.setProperty("--bs-light-rgb", UI_COLORS.bootstrap.lightRgbDark);
    root.style.setProperty("--bs-dark-rgb", UI_COLORS.bootstrap.darkRgbDark);
    root.style.setProperty("--bs-border-opacity", UI_COLORS.bootstrap.borderOpacity);
  } else {
    root.style.setProperty("--bs-light-rgb", UI_COLORS.bootstrap.lightRgb);
    root.style.setProperty("--bs-dark-rgb", UI_COLORS.bootstrap.darkRgb);
    root.style.setProperty("--bs-border-opacity", "1");
  }
  
  // Bootstrap Link Colors
  root.style.setProperty("--bs-link-color", theme.palette.primary.main);
  root.style.setProperty("--bs-link-hover-color", theme.palette.primary.dark || theme.palette.primary.main);
  
  // Bootstrap Emphasis Color
  root.style.setProperty("--bs-emphasis-color", theme.palette.text.primary);
  
  // Bootstrap Code Colors
  root.style.setProperty("--bs-code-color", theme.palette.mode === "dark" 
    ? UI_COLORS.bootstrap.codeColorDark
    : UI_COLORS.bootstrap.codeColor);
  
  // Bootstrap Form Control Colors
  root.style.setProperty("--bs-form-control-bg", theme.palette.background.paper);
  root.style.setProperty("--bs-form-control-color", theme.palette.text.primary);
  root.style.setProperty("--bs-form-control-border-color", borderColor);
  
  // Bootstrap Focus Colors (reuse primaryRgb from above)
  root.style.setProperty("--bs-focus-ring-color", `rgba(${primaryRgb}, 0.25)`);
  
  // Bootstrap Accordion Colors - use primary color for active state
  root.style.setProperty("--bs-accordion-active-color", theme.palette.primary.main);
  
  // Mode
  root.style.setProperty("--theme-mode", theme.palette.mode);
  
  // Error, Warning, Info, Success
  if (theme.palette.error) {
    root.style.setProperty("--theme-error", theme.palette.error.main);
  }
  if (theme.palette.warning) {
    root.style.setProperty("--theme-warning", theme.palette.warning.main);
  }
  if (theme.palette.info) {
    root.style.setProperty("--theme-info", theme.palette.info.main);
  }
  if (theme.palette.success) {
    root.style.setProperty("--theme-success", theme.palette.success.main);
  }
  
  // Add data attributes for CSS selectors
  root.setAttribute("data-theme", theme.palette.mode);
  // Also set Bootstrap's theme attribute for compatibility
  root.setAttribute("data-bs-theme", theme.palette.mode);
};

/**
 * Custom ThemeProvider that wraps MUI's ThemeProvider
 * and provides theme based on Redux state
 */
const THEME_STORAGE_KEY = "theme";

const ThemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const themeState = useSelector((state) => state.theme);
  const [systemPreference, setSystemPreference] = useState(getBrowserThemePreference());

  // Other tabs (e.g. /pam/file_manager) share localStorage but not Redux — sync when theme changes elsewhere.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== THEME_STORAGE_KEY || e.newValue == null) {
        return;
      }
      try {
        const parsed = JSON.parse(e.newValue);
        dispatch(hydrateThemeFromStorage(parsed));
      } catch {
        // ignore invalid JSON
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);

  useEffect(() => {
    const updateSystemPreference = () => {
      const currentPreference = getBrowserThemePreference();
      setSystemPreference(currentPreference);
    };
    
    updateSystemPreference();
    
    if (themeState.mode !== "system") {
      return;
    }
    
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = (e) => {
      setSystemPreference(e.matches ? "dark" : "light");
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [themeState.mode]);
  

  const theme = useMemo(() => {
    return createAppTheme(themeState, systemPreference);
  }, [themeState, systemPreference]);

  // Inject CSS variables and apply body styles whenever theme changes
  useEffect(() => {
    injectCSSVariables(theme);
    
    // Directly apply styles to body element for immediate effect
    const body = document.body;
    if (body) {
      body.style.backgroundColor = theme.palette.background.default;
      body.style.color = theme.palette.text.primary;
    }
    
    // Apply to html element as well
    const html = document.documentElement;
    if (html) {
      html.style.backgroundColor = theme.palette.background.default;
      html.style.color = theme.palette.text.primary;
    }
  }, [theme]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline 
        enableColorScheme
        style={{
          body: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
        }}
      />
      {children}
    </MuiThemeProvider>
  );
};

export default ThemeProvider;


